<template>
  <div class="orders-page">
    <div class="filter-bar">
      <div class="search-box">
        <input 
          type="text" 
          v-model="searchKey" 
          placeholder="搜索订单号、用户手机号..."
          class="search-input"
          @keyup.enter="search"
        />
        <button @click="search" class="search-btn">搜索</button>
      </div>
      <div class="filter-selects">
        <select v-model="orderStatus" class="filter-select" @change="search">
          <option value="">全部状态</option>
          <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>
        <select v-model="serviceType" class="filter-select" @change="search">
          <option value="">全部服务</option>
          <option v-for="opt in serviceOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>
        <input 
          type="text" 
          v-model="runnerName" 
          placeholder="跑手姓名/手机..."
          class="search-input runner-input"
          @keyup.enter="search"
          @change="search"
        />
        <select v-model="runnerId" class="filter-select" @change="search">
          <option value="">全部跑手</option>
          <option v-for="runner in runnerOptions" :key="runner.id" :value="runner.id">{{ runner.name }}</option>
        </select>
        <input 
          type="date" 
          v-model="startDate" 
          class="date-input"
          @change="search"
        />
        <input 
          type="date" 
          v-model="endDate" 
          class="date-input"
          @change="search"
        />
      </div>
    </div>

    <div class="table-container">
      <table class="orders-table">
        <thead>
          <tr>
            <th>订单号</th>
            <th>服务类型</th>
            <th>下单用户</th>
            <th>跑手姓名</th>
            <th>金额</th>
            <th>状态</th>
            <th>下单时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="order in orders" :key="order.id">
            <td>{{ order.order_no }}</td>
            <td>{{ order.service_type_text || getServiceName(order.service_type_code) }}</td>
            <td>
              <div class="order-userinfo">
                <div class="order-community-name">{{ order.community_name }} - {{ order.shop_name }}</div>
                <div class="order-user-name">{{ order.user_name }} {{ order.phone }}</div>
              </div>
            </td>
            <td>{{ order.runner_name || '-' }}</td>
            <td>{{ centToYuanTrim(order.pay_amount) }}</td>
            <td><span :class="'status ' + getStatusClass(order.order_status)">{{ getStatusName(order.order_status) }}</span></td>
            <td>{{ order.created_at ? formatDateTime(order.created_at) : '-' }}</td>
            <td>
              <button @click="viewDetail(order.id)" class="action-btn view">查看</button>
              <button v-if="order.order_status === 0" @click="cancelOrder(order.id)" class="action-btn cancel">取消</button>
            </td>
          </tr>
          <tr v-if="orders.length === 0">
            <td colspan="8" class="empty-tip">暂无数据</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pagination">
      <button :disabled="currentPage === 1" @click="prevPage" class="page-btn">上一页</button>
      <span class="page-info">第 {{ currentPage }} / {{ totalPages }} 页</span>
      <button :disabled="currentPage === totalPages" @click="nextPage" class="page-btn">下一页</button>
    </div>
  </div>
</template>

<script>
import { getOrderList, updateOrderStatus } from '@/api/orders'
import { getRunnerList } from '@/api/runners'
import { getEnums } from '@/api/system'
import { centToYuanTrim } from '@/utils/money'
import { formatDateTime } from '@/utils/date'

const STATUS_CLASS_MAP = {
  0: 'status-pending-pay',
  1: 'status-pending-accept',
  2: 'status-pending-pickup',
  3: 'status-pending-deliver',
  4: 'status-pending-confirm',
  5: 'status-completed'
}

export default {
  name: 'Orders',
  data() {
    return {
      searchKey: '',
      orderStatus: '',
      serviceType: '',
      runnerName: '',
      runnerId: '',
      runnerOptions: [],
      startDate: '',
      endDate: '',
      currentPage: 1,
      totalPages: 1,
      orders: [],
      statusMap: {},
      serviceMap: {},
      statusOptions: [],
      serviceOptions: []
    }
  },
  mounted() {
    this.loadRunnerOptions()
    this.loadEnums().then(() => this.fetchOrders())
  },
  methods: {
    centToYuanTrim,
    formatDateTime,
    async loadEnums() {
      try {
        const res = await getEnums()
        if (res.code === 0) {
          this.statusMap = res.data.orderStatusMap || {}
          this.serviceMap = res.data.serviceTypeMap || {}
          this.statusOptions = Object.entries(this.statusMap).map(([value, label]) => ({ value, label }))
          this.serviceOptions = Object.entries(this.serviceMap).map(([value, label]) => ({ value, label }))
        }
      } catch (error) {
      }
    },
    async loadRunnerOptions() {
      try {
        const res = await getRunnerList({ limit: 1000 })
        if (res.code === 0) {
          this.runnerOptions = res.data.list || []
        }
      } catch (error) {
      }
    },
    getServiceName(type) {
      return this.serviceMap[type] || '未知'
    },
    getStatusName(status) {
      return this.statusMap[status] || '未知'
    },
    getStatusClass(status) {
      return STATUS_CLASS_MAP[status] || ''
    },
    async fetchOrders() {
      const params = {
        page: this.currentPage,
        pageSize: 20
      }
      if (this.searchKey) {
        params.order_no = this.searchKey
        params.phone = this.searchKey
      }
      if (this.orderStatus !== '') {
        params.order_status = parseInt(this.orderStatus)
      }
      if (this.serviceType !== '') {
        params.service_type_code = this.serviceType
      }
      if (this.runnerId) {
        params.runner_id = parseInt(this.runnerId)
      } else if (this.runnerName) {
        params.runner_name = this.runnerName
      }
      if (this.startDate) {
        params.start_date = this.startDate
      }
      if (this.endDate) {
        params.end_date = this.endDate
      }

      try {
        const res = await getOrderList(params)
        if (res.code === 0) {
          this.orders = res.data.data || []
          this.currentPage = res.data.current_page || 1
          this.totalPages = res.data.last_page || 1
        }
      } catch (error) {
      }
    },
    search() {
      this.currentPage = 1
      this.fetchOrders()
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        this.fetchOrders()
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
        this.fetchOrders()
      }
    },
    viewDetail(id) {
      this.$router.push(`/orders/detail/${id}`)
    },
    async cancelOrder(id) {
      if (confirm('确定要取消该订单吗？')) {
        try {
          const res = await updateOrderStatus(id, 4)
          if (res.code === 0) {
            alert('取消成功')
            this.fetchOrders()
          } else {
            alert(res.message || '操作失败')
          }
        } catch (error) {
          alert('操作失败')
        }
      }
    }
  }
}
</script>

<style scoped>
.orders-page {
  padding: 20px;
}

.filter-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.search-box {
  display: flex;
  gap: 10px;
}

.search-input {
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  width: 250px;
}

.search-btn {
  padding: 10px 20px;
  background: #3498db;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.filter-selects {
  display: flex;
  gap: 15px;
  align-items: center;
}

.filter-select, .date-input {
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
}

.runner-input {
  width: 160px;
}

.table-container {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  overflow: hidden;
}

.orders-table {
  width: 100%;
  border-collapse: collapse;
}

.orders-table th, .orders-table td {
  padding: 14px 16px;
  text-align: left;
  border-bottom: 1px solid #eee;
  font-size: 14px;
}

.orders-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
}

.empty-tip {
  text-align: center;
  color: #999;
  padding: 40px;
}

.status {
  padding: 4px 0;
  border-radius: 20px;
  font-size: 12px;
}

.status-pending-pay    { background: #fff3e0; color: #f39c12; }  /* 0 待付款 */
.status-pending-accept  { background: #ffebee; color: #e74c3c; }  /* 1 待接单 */
.status-pending-pickup  { background: #e8f5e9; color: #2ecc71; }  /* 2 待取件 */
.status-pending-deliver { background: #f5f5f5; color: #666;    }  /* 3 待送达 */
.status-pending-confirm { background: #e3f2fd; color: #3498db; }  /* 4 待确认 */
.status-completed       { background: #fff; color: #333;}  /* 5 已完成 */

.action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  margin-right: 5px;
}

.action-btn.view {
  background: #3498db;
  color: #fff;
}

.order-userinfo {
  display: flex;
  flex-direction: column;
}

.order-community-name {
  font-size: 12px;
  color: #333;
}

.order-user-name {
  font-size: 12px;
  color: #888;
}

.action-btn.cancel {
  background: #e74c3c;
  color: #fff;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 20px;
}

.page-btn {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #666;
}
</style>
